import {
  type Icon,
  BinocularsIcon,
  BriefcaseIcon,
  ChatsCircleIcon,
  ChatsIcon,
  MegaphoneIcon,
  MicrophoneIcon,
  MicrophoneStageIcon,
  NewspaperClippingIcon,
  NewspaperIcon,
  QuotesIcon,
  ShieldCheckIcon,
  ShieldStarIcon,
  SunIcon,
  TrophyIcon,
} from '@phosphor-icons/react';

import type { ArtifactType } from './types';

/** Lowercase noun pair for group-scoped copy, e.g. the grouped-table
 * expander row ("Show 3 more win-loss stories", "Show 1 more objection" —
 * Reshuffle v2 table spec §4). Sentence-medial casing; surfaces add their
 * own leading copy. */
export type ArtifactGroupNoun = {
  singular: string;
  plural: string;
};

export type ArtifactRegistryEntry = {
  /** Short label used in filter dropdowns */
  label: string;
  /** Full label used inside the ArtifactTypeChip on detail views */
  typeChipLabel: string;
  /** Short label used inside the ArtifactTypeChip on tiles and list items */
  shortTypeChipLabel: string;
  /** Noun for group-count copy ("Show {n} more {plural}") */
  groupNoun: ArtifactGroupNoun;
  /** Phosphor icon component for this artifact type */
  icon: Icon;
  /** Mantine color shade (e.g. 'cyan.6') used for icon + chip text */
  color: string;
};

export const ARTIFACT_REGISTRY: Record<ArtifactType, ArtifactRegistryEntry> = {
  news_item: {
    label: 'News',
    typeChipLabel: 'News Artifact',
    shortTypeChipLabel: 'News',
    groupNoun: { singular: 'news story', plural: 'news stories' },
    icon: NewspaperIcon,
    color: 'cyan.6',
  },
  news_rollup: {
    label: 'News Digest',
    typeChipLabel: 'News Digest',
    shortTypeChipLabel: 'News Digest',
    groupNoun: { singular: 'news digest', plural: 'news digests' },
    icon: NewspaperClippingIcon,
    color: 'cyan.7',
  },
  prospect_quote: {
    label: 'Quotes',
    typeChipLabel: 'Prospect Quote',
    shortTypeChipLabel: 'Quote',
    groupNoun: { singular: 'quote', plural: 'quotes' },
    icon: QuotesIcon,
    color: 'violet.6',
  },
  prospects_quotes_rollup: {
    label: 'Trending Quotes',
    typeChipLabel: 'Trending Quotes',
    shortTypeChipLabel: 'Trending Quotes',
    groupNoun: { singular: 'trending quote', plural: 'trending quotes' },
    icon: ChatsIcon,
    color: 'violet.7',
  },
  objection_handling_quote: {
    label: 'Objections',
    typeChipLabel: 'Objection Handling',
    shortTypeChipLabel: 'Objection',
    groupNoun: { singular: 'objection', plural: 'objections' },
    icon: ShieldCheckIcon,
    color: 'orange.6',
  },
  objection_handling_rollup: {
    label: 'Trending Objections',
    typeChipLabel: 'Trending Objections',
    shortTypeChipLabel: 'Trending Objections',
    groupNoun: { singular: 'objection', plural: 'objections' },
    icon: ShieldStarIcon,
    color: 'orange.7',
  },
  winloss_story: {
    label: 'Win-Loss',
    typeChipLabel: 'Win-Loss Story',
    shortTypeChipLabel: 'Win-Loss',
    groupNoun: { singular: 'win-loss story', plural: 'win-loss stories' },
    icon: TrophyIcon,
    color: 'cyan.6',
  },
  winloss_rollup: {
    label: 'Win-Loss Trends',
    typeChipLabel: 'Win-Loss Trends',
    shortTypeChipLabel: 'WL Trends',
    groupNoun: { singular: 'win-loss trend', plural: 'win-loss trends' },
    icon: TrophyIcon,
    color: 'cyan.7',
  },
  talk_tracks: {
    label: 'Talk Tracks',
    typeChipLabel: 'Talk Track',
    shortTypeChipLabel: 'Talk Track',
    groupNoun: { singular: 'talk track', plural: 'talk tracks' },
    icon: MicrophoneStageIcon,
    color: 'cyan.6',
  },
  talktracks_rollup: {
    label: 'Talk Track Trends',
    typeChipLabel: 'Talk Track Trends',
    shortTypeChipLabel: 'TT Trends',
    groupNoun: { singular: 'talk track trend', plural: 'talk track trends' },
    icon: MegaphoneIcon,
    color: 'cyan.7',
  },
  daily_brief: {
    label: 'Daily Brief',
    typeChipLabel: 'Daily Brief',
    shortTypeChipLabel: 'Brief',
    groupNoun: { singular: 'daily brief', plural: 'daily briefs' },
    icon: SunIcon,
    color: 'gray.7',
  },
  customer_quote: {
    label: 'Customer Quotes',
    typeChipLabel: 'Customer Quote',
    shortTypeChipLabel: 'Customer Quote',
    groupNoun: { singular: 'customer quote', plural: 'customer quotes' },
    icon: ChatsCircleIcon,
    // TODO: this is a default Mantine color, and not in our design-tokens. Either update it to use a supported color or reach out to #epd-designers.
    color: 'pink.6',
  },
  customer_quotes_rollup: {
    label: 'Customer Proof Trends',
    typeChipLabel: 'Customer Proof Trends',
    shortTypeChipLabel: 'Proof Trends',
    groupNoun: {
      singular: 'customer proof trend',
      plural: 'customer proof trends',
    },
    icon: ChatsCircleIcon,
    // TODO: this is a default Mantine color, and not in our design-tokens. Either update it to use a supported color or reach out to #epd-designers.
    color: 'pink.7',
  },
  blindspots: {
    label: 'Blindspots',
    typeChipLabel: 'Blindspots Study',
    shortTypeChipLabel: 'Blindspots',
    groupNoun: { singular: 'blindspots study', plural: 'blindspots studies' },
    icon: BinocularsIcon,
    color: 'cyan.7',
  },
  hiring_signal_block: {
    label: 'Hiring Signals',
    typeChipLabel: 'Hiring Signal',
    shortTypeChipLabel: 'Hiring',
    groupNoun: { singular: 'hiring signal', plural: 'hiring signals' },
    icon: BriefcaseIcon,
    // TODO: this is a default Mantine color, and not in our design-tokens. Either update it to use a supported color or reach out to #epd-designers.
    color: 'indigo.6',
  },
  // Topics rollups use stak's editorial display names (header resolver,
  // stak#2090) so list rows, chips, and the backend eyebrow agree —
  // "Themes" also distinguishes them from their `*_rollup` "Trends"
  // predecessors in the type filter dropdown.
  prospect_quotes_rollup_topics: {
    label: 'Buyer Quote Themes',
    typeChipLabel: 'Buyer Quote Themes',
    shortTypeChipLabel: 'Quote Themes',
    groupNoun: { singular: 'buyer quote theme', plural: 'buyer quote themes' },
    icon: ChatsIcon,
    color: 'violet.7',
  },
  objection_handling_rollup_topics: {
    label: 'Objection Themes',
    typeChipLabel: 'Objection Themes',
    shortTypeChipLabel: 'Objection Themes',
    groupNoun: { singular: 'objection theme', plural: 'objection themes' },
    icon: ShieldStarIcon,
    color: 'orange.7',
  },
  talk_tracks_rollup_topics: {
    label: 'Talk Track Themes',
    typeChipLabel: 'Talk Track Themes',
    shortTypeChipLabel: 'TT Themes',
    groupNoun: { singular: 'talk track theme', plural: 'talk track themes' },
    icon: MegaphoneIcon,
    color: 'cyan.7',
  },
  winloss_rollup_topics: {
    label: 'Win-Loss Themes',
    typeChipLabel: 'Win-Loss Themes',
    shortTypeChipLabel: 'WL Themes',
    groupNoun: { singular: 'win-loss theme', plural: 'win-loss themes' },
    icon: TrophyIcon,
    color: 'cyan.7',
  },
  proof_points: {
    label: 'Proof Points',
    typeChipLabel: 'Proof Point',
    shortTypeChipLabel: 'Proof Point',
    groupNoun: { singular: 'proof point', plural: 'proof points' },
    icon: ChatsCircleIcon,
    // TODO: this is a default Mantine color, and not in our design-tokens. Either update it to use a supported color or reach out to #epd-designers.
    color: 'pink.6',
  },
  campaign_interview_analysis: {
    label: 'Win-Loss Interview',
    typeChipLabel: 'Win-Loss Interview',
    shortTypeChipLabel: 'Interview',
    groupNoun: {
      singular: 'win-loss interview',
      plural: 'win-loss interviews',
    },
    icon: MicrophoneIcon,
    color: 'cyan.5',
  },
  campaign_rollup_analysis: {
    label: 'Win-Loss Campaign Summary',
    typeChipLabel: 'Win-Loss Campaign Summary',
    shortTypeChipLabel: 'Campaign Summary',
    groupNoun: {
      singular: 'win-loss campaign summary',
      plural: 'win-loss campaign summaries',
    },
    icon: TrophyIcon,
    color: 'cyan.5',
  },
};
